import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

/** Set by paste sanitizing when the source table declared a visible grid. */
export const PASTED_DATA_TABLE_ATTR = 'data-ss-grid';

/**
 * Hex, rgb/rgba, or a plain color name. Rejects `url(`, `var(`, and
 * `expression` so a pasted fill cannot pull in a remote image or a theme token.
 */
const SAFE_CELL_PAINT = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%]+\)|[a-z]+)$/i;

export function safeCellPaint(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const color = value.trim();
  if (
    color.length === 0 ||
    /expression|url\s*\(|var\s*\(/i.test(color) ||
    !SAFE_CELL_PAINT.test(color)
  ) {
    return null;
  }
  return color;
}

function declaredStyleValue(
  element: HTMLElement,
  prop: 'background-color' | 'color',
): string {
  if (prop === 'color') {
    if (element.style.color) {
      return element.style.color;
    }
    const style = element.getAttribute('style') ?? '';
    return /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(style)?.[1]?.trim() ?? '';
  }
  if (element.style.backgroundColor) {
    return element.style.backgroundColor;
  }
  const style = element.getAttribute('style') ?? '';
  const fromStyle =
    /(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i.exec(style)?.[1]?.trim() ??
    '';
  if (fromStyle) {
    return fromStyle;
  }
  return element.getAttribute('bgcolor') ?? '';
}

/**
 * Stock TipTap cells only persist colspan, rowspan, colwidth, and align.
 * Align's render writes `style`, which replaced the cell fill, so a white
 * header on a dark background came out blank.
 */
function cellPaintAttributes() {
  return {
    backgroundColor: {
      default: null,
      parseHTML: (element: HTMLElement) =>
        safeCellPaint(declaredStyleValue(element, 'background-color')),
      renderHTML: (attributes: { backgroundColor?: unknown }) => {
        const value = safeCellPaint(
          typeof attributes.backgroundColor === 'string'
            ? attributes.backgroundColor
            : null,
        );
        if (!value) {
          return {};
        }
        return { style: `background-color: ${value}` };
      },
    },
    textColor: {
      default: null,
      parseHTML: (element: HTMLElement) =>
        safeCellPaint(declaredStyleValue(element, 'color')),
      renderHTML: (attributes: { textColor?: unknown }) => {
        const value = safeCellPaint(
          typeof attributes.textColor === 'string'
            ? attributes.textColor
            : null,
        );
        if (!value) {
          return {};
        }
        return { style: `color: ${value}` };
      },
    },
  };
}

export const SiteScopeTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ...cellPaintAttributes(),
    };
  },
});

export const SiteScopeTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ...cellPaintAttributes(),
    };
  },
});

/**
 * TipTap's stock table node drops unknown attributes. Keep the paste marker
 * so editor and read-only CSS can draw a border only on document grids.
 */
export const SiteScopeTable = Table.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      ssGrid: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute(PASTED_DATA_TABLE_ATTR),
        renderHTML: (attributes: { ssGrid?: unknown }) => {
          const value = attributes.ssGrid;
          if (typeof value !== 'string' || value.length === 0) {
            return {};
          }
          return { [PASTED_DATA_TABLE_ATTR]: value };
        },
      },
    };
  },
});
